import { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createDevTicket,
  getMyDevTickets,
} from "../../../redux/requests/Dev-TicketsRequests";
import { searchTicketsShort } from "../../../redux/requests/TicketRequests";
import { AppDispatch, RootState } from "../../../redux/Store";
import { devTicketSlice } from "../../../redux/reducers/Dev-TicketsSlice";
import { ticketSlice } from "../../../redux/reducers/TicketSlice";
import "./Developer-tickers.css";
import { toast } from "react-toastify";
import { RootOptions } from "react-dom/client";

interface ICreateDeveloperTicketProps {
  ticketId?: number;
}

const CreateDeveloperTicket: FC<ICreateDeveloperTicketProps> = ({
  ticketId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    myDevTickets,
    devTicketActionLoading,
    isLoading,
    error,
    selectedDevTicket,
  } = useSelector((state: RootState) => state.devTicket);

  const { searchTickets, searchTicketsLoading } = useSelector(
    (state: RootState) => state.tickets,
  );

  const { operator } = useSelector((state: RootState) => state.operator);
  const isCenterOperator = operator?.role === "CENTER_OPERATOR";

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [form, setForm] = useState({
    ticketId: ticketId || undefined,
    question: "",
    operatorComment: "",
    phoneNumber: "",
  });
  const [files, setFiles] = useState<File[]>([]);

  const [ticketSearch, setTicketSearch] = useState("");
  const [debouncedTicketSearch, setDebouncedTicketSearch] = useState("");
  const [showTicketDropdown, setShowTicketDropdown] = useState(false);

  useEffect(() => {
    dispatch(getMyDevTickets());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDevTicket) {
      const timer = setTimeout(() => {
        dispatch(devTicketSlice.actions.clearSelectedDevTicket());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [selectedDevTicket, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTicketSearch(ticketSearch);
    }, 400);

    return () => clearTimeout(timer);
  }, [ticketSearch]);

  useEffect(() => {
    const q = debouncedTicketSearch.trim();

    if (!q) {
      dispatch(ticketSlice.actions.clearSearchTickets());
      return;
    }

    dispatch(searchTicketsShort(q));
    setShowTicketDropdown(true);
  }, [debouncedTicketSearch, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowTicketDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openModal = () => {
    dispatch(devTicketSlice.actions.clearSelectedDevTicket());
    dispatch(ticketSlice.actions.clearSearchTickets());

    setForm({
      ticketId: ticketId || undefined,
      question: "",
      operatorComment: "",
      phoneNumber: "",
    });

    setFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setTicketSearch("");
    setDebouncedTicketSearch("");
    setShowTicketDropdown(false);

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);

    setForm({
      ticketId: ticketId || undefined,
      question: "",
      operatorComment: "",
      phoneNumber: "",
    });

    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setTicketSearch("");
    setDebouncedTicketSearch("");
    setShowTicketDropdown(false);

    dispatch(ticketSlice.actions.clearSearchTickets());
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];

    const invalidFiles = selectedFiles.filter(
      (file) => !allowedTypes.includes(file.type),
    );

    if (invalidFiles.length > 0) {
      toast.warning("Можно загрузить только PNG, JPEG, JPG или PDF");
      e.target.value = "";
      return;
    }

    setFiles(selectedFiles);
  };

  const removeFile = (fileIndex: number) => {
    setFiles((prev) => {
      const updatedFiles = prev.filter((_, index) => index !== fileIndex);

      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();

        updatedFiles.forEach((file) => {
          dataTransfer.items.add(file);
        });

        fileInputRef.current.files = dataTransfer.files;
      }

      return updatedFiles;
    });
  };

  const onSelectTicket = (item: any) => {
    setForm((prev) => ({
      ...prev,
      ticketId: item.id,
    }));

    setTicketSearch(`${item.id}`);

    setShowTicketDropdown(false);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const manualTicketId = /^\d+$/.test(ticketSearch.trim())
      ? Number(ticketSearch.trim())
      : undefined;

    const finalTicketId = form.ticketId || manualTicketId;

    if (!form.question.trim()) {
      toast.warning("Напишите вопрос");
      return;
    }

    if (!finalTicketId && !form.phoneNumber.trim()) {
      toast.warning(
        isCenterOperator
          ? "Укажите номер телефона"
          : "Укажите ticketId или номер телефона",
      );
      return;
    }

    try {
      const formData = new FormData();

      formData.append("question", form.question);

      if (form.operatorComment) {
        formData.append("operatorComment", form.operatorComment);
      }

      if (finalTicketId) {
        formData.append("ticketId", String(finalTicketId));
      }

      if (form.phoneNumber) {
        formData.append("phoneNumber", form.phoneNumber);
      }

      files.forEach((file) => {
        formData.append("files", file);
      });

      await dispatch(createDevTicket(formData));

      toast.success("Заявка отправлена в технический отдел");
      closeModal();
      dispatch(getMyDevTickets());
    } catch (error: any) {
      toast.error(error.message || "Ошибка отправки заявки");
    }
  };

  const getStatusClassName = (status: string) => {
    if (status === "Completed") {
      return "operator-dev-tickets__status-badge operator-dev-tickets__status-badge--completed";
    }

    return "operator-dev-tickets__status-badge operator-dev-tickets__status-badge--in-process";
  };

  return (
    <div className="operator-dev-tickets">
      <div className="operator-dev-tickets__header">
        <div>
          <h2 className="operator-dev-tickets__title">Технические заявки</h2>
          <p className="operator-dev-tickets__subtitle">
            Здесь вы можете смотреть статус своих заявок и отправлять новые
            вопросы
          </p>
        </div>

        <button
          className="operator-dev-tickets__create-button"
          onClick={openModal}
        >
          Отправить вопрос
        </button>
      </div>

      {isLoading && (
        <p className="operator-dev-tickets__info-text">Загрузка...</p>
      )}

      {error && <p className="operator-dev-tickets__error-text">{error}</p>}

      {!isLoading && myDevTickets?.length === 0 && (
        <p className="operator-dev-tickets__info-text">
          У вас пока нет заявок для теххнического отдела
        </p>
      )}

      {!isLoading && myDevTickets?.length > 0 && (
        <div className="operator-dev-tickets__table-wrapper">
          <table className="operator-dev-tickets__table">
            <thead>
              <tr>
                <th className="operator-dev-tickets__th">Вопрос</th>
                <th className="operator-dev-tickets__th">
                  Комментарий оператора
                </th>
                <th className="operator-dev-tickets__th">
                  Комментарий тех. отдела
                </th>
                <th className="operator-dev-tickets__th">Телефон</th>
                <th className="operator-dev-tickets__th">Тикет</th>
                <th className="operator-dev-tickets__th">Статус</th>
                <th className="operator-dev-tickets__th">Дата создания</th>
                <th className="operator-dev-tickets__th">Дата закрытия</th>
              </tr>
            </thead>
            <tbody>
              {myDevTickets.map((item) => (
                <tr key={item.id}>
                  <td className="operator-dev-tickets__td">
                    <div className="operator-dev-tickets__main-text">
                      {item.question}
                    </div>
                  </td>

                  <td className="operator-dev-tickets__td">
                    {item.operatorComment || "-"}
                  </td>

                  <td className="operator-dev-tickets__td">
                    {item.developerComment || "-"}
                  </td>

                  <td className="operator-dev-tickets__td">
                    {item.phoneNumber || "-"}
                  </td>

                  <td className="operator-dev-tickets__td">
                    {item.ticketId ?? "-"}
                  </td>

                  <td className="operator-dev-tickets__td">
                    <span className={getStatusClassName(item.status)}>
                      {item.status === "Completed" ? "Завершено" : "В работе"}
                    </span>
                  </td>

                  <td className="operator-dev-tickets__td">
                    {new Date(item.createdAt).toLocaleString()}
                  </td>

                  <td className="operator-dev-tickets__td">
                    {item.completedAt
                      ? new Date(item.completedAt).toLocaleString()
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="operator-dev-tickets-modal-overlay">
          <div className="operator-dev-tickets-modal">
            <h3 className="operator-Фdev-tickets-modal__title">
              Отправить вопрос в технический отдел
            </h3>
            <p className="operator-dev-tickets-modal__subtitle">
              Найдите заявку или укажите номер телефона, затем опишите проблему
            </p>

            <form onSubmit={onSubmit}>
              {!isCenterOperator && (
                <div
                  className="operator-dev-tickets-modal__field"
                  ref={dropdownRef}
                >
                  <label className="operator-dev-tickets-modal__label">
                    Поиск заявки
                  </label>

                  <input
                    type="text"
                    value={ticketSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTicketSearch(value);
                      setShowTicketDropdown(true);

                      if (!value.trim()) {
                        setForm((prev) => ({
                          ...prev,
                          ticketId: undefined,
                        }));
                        return;
                      }

                      if (/^\d+$/.test(value.trim())) {
                        setForm((prev) => ({
                          ...prev,
                          ticketId: Number(value.trim()),
                        }));
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          ticketId: undefined,
                        }));
                      }
                    }}
                    className="operator-dev-tickets-modal__input"
                    placeholder="Введите id, имя клиента или вопрос"
                  />

                  {showTicketDropdown && (
                    <div className="ticket-search-dropdown">
                      {searchTicketsLoading && (
                        <div className="ticket-search-dropdown__empty">
                          Загрузка...
                        </div>
                      )}

                      {!searchTicketsLoading &&
                        ticketSearch.trim() &&
                        searchTickets.length === 0 && (
                          <div className="ticket-search-dropdown__empty">
                            Ничего не найдено
                          </div>
                        )}

                      {!searchTicketsLoading &&
                        searchTickets.map((item) => (
                          <div
                            key={item.id}
                            className="ticket-search-dropdown__item"
                            onClick={() => onSelectTicket(item)}
                          >
                            <div className="ticket-search-dropdown__name">
                              {item.user?.name || "-"}
                            </div>

                            <div className="ticket-search-dropdown__question">
                              {item.question || "-"}
                            </div>

                            <div className="ticket-search-dropdown__project">
                              Проект: {item.project || "-"}
                            </div>

                            <div className="ticket-search-dropdown__id">
                              Ticket ID: #{item.id}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              <div className="operator-dev-tickets-modal__field">
                <label className="operator-dev-tickets-modal__label">
                  Номер телефона
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={onChange}
                  className="operator-dev-tickets-modal__input"
                  placeholder="Если вопрос по звонку"
                />
              </div>

              <div className="operator-dev-tickets-modal__field">
                <label className="operator-dev-tickets-modal__label">
                  Вопрос в технический отдел
                </label>
                <input
                  type="text"
                  name="question"
                  value={form.question}
                  onChange={onChange}
                  className="operator-dev-tickets-modal__input"
                  placeholder="Коротко опишите проблему"
                />
              </div>

              <div className="operator-dev-tickets-modal__field">
                <label className="operator-dev-tickets-modal__label">
                  Комментарий оператора
                </label>
                <textarea
                  name="operatorComment"
                  value={form.operatorComment}
                  onChange={onChange}
                  className="operator-dev-tickets-modal__textarea"
                  placeholder="Подробно опишите проблему"
                />
              </div>

              <div className="operator-dev-tickets-modal__field">
                <label className="operator-dev-tickets-modal__label">
                  Файлы
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                  onChange={onFilesChange}
                  className="operator-dev-tickets-modal__input"
                />

                {files.length > 0 && (
                  <div className="operator-dev-tickets-modal__files">
                    {files.map((file, index) => (
                      <div
                        key={`${file.name}-${file.size}-${index}`}
                        className="operator-dev-tickets-modal__file"
                      >
                        <span>{file.name}</span>

                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="operator-dev-tickets-modal__file-remove"
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="operator-dev-tickets-modal__actions">
                <button
                  type="button"
                  className="operator-dev-tickets-modal__cancel-button"
                  onClick={closeModal}
                  disabled={devTicketActionLoading}
                >
                  Отмена
                </button>

                <button
                  type="submit"
                  className="operator-dev-tickets-modal__submit-button"
                  disabled={devTicketActionLoading}
                >
                  {devTicketActionLoading
                    ? "Отправка..."
                    : "Отправить в технический отдел"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDeveloperTicket;
